import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import UserDepartmentSelect from '~/components/Shared/UserDepartmentSelect/UserDepartmentSelect';
import { EntityType } from '~/enums/EntityType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { SaveSearchPermissionType } from '~/enums/SaveSearchPermissionType';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { useIntl } from 'react-intl';
import * as selectors from '~/redux/selectors';
import CharacterLimit from '~/ui/components/CharacterLimit/CharacterLimit';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import SingleSelect from '~/ui/components/SingleSelect/SingleSelect';
import TextInput from '~/ui/components/TextInput/TextInput';
import { CHARACTER_INPUT_LIMIT_SHORT } from '~/ui/enums/input';

import { getUserDepartmentArray } from '../SearchV2.utils';

const SaveSearchForm = ({ isDuplicate = false, handleFormDataChanges }) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const hasSearchV2Flag = useHasFeatureFlag(FeatureFlagType.SearchV2);
  const modal = useSelector(selectors.selectSearchResultModal);
  const { context } = modal || {};

  const defaultPermissionBasedOnFlag = hasSearchV2Flag
    ? SaveSearchPermissionType.Private
    : SaveSearchPermissionType.Open;
  const getDefaultPermissionOption = () =>
    context?.search_visibility || defaultPermissionBasedOnFlag;

  const getSaveSearchPermissionOptions = (clientName) => {
    return [
      {
        label: intl.formatMessage({
          id: 'SaveSearchForm.onlyMePrivate',
          defaultMessage: 'Only me (private)',
          description: 'Private permission option for saved search',
        }),
        value: SaveSearchPermissionType.Private,
      },
      {
        label: intl.formatMessage({
          id: 'SaveSearchForm.specificDepartmentsUsers',
          defaultMessage: 'Specific departments (groups) / users',
          description: 'Shared permission option for saved search',
        }),
        value: SaveSearchPermissionType.Shared,
      },
      {
        label: intl.formatMessage(
          {
            id: 'SaveSearchForm.anyoneAtClient',
            defaultMessage: 'Anyone at {clientName}',
            description:
              'Open permission option for saved search. Example: clientName = "Document Name"',
          },
          { clientName },
        ),
        value: SaveSearchPermissionType.Open,
      },
    ];
  };

  const getPlaceholderName = (savedSearch) =>
    isDuplicate
      ? intl.formatMessage(
          {
            id: 'SaveSearchForm.copyOfName',
            defaultMessage: 'Copy of {name}',
            description:
              'Prefix for duplicated search names. Example: name = "Document Name"',
          },
          { name: savedSearch?.name },
        )
      : savedSearch?.name;
  const PERMISSION_OPTIONS = getSaveSearchPermissionOptions(
    currentUser.client_name,
  );
  const usersDepartmentsArray = getUserDepartmentArray(context);

  const [name, setName] = useState(context?.name);
  const [selectedItemPermission, setSelectedItemPermission] = useState(
    getDefaultPermissionOption(),
  );
  const [userDepartments, setUsersDepartments] = useState(
    usersDepartmentsArray,
  );

  const isSharedPermissionSelected =
    selectedItemPermission === SaveSearchPermissionType.Shared;

  useEffect(() => {
    setName(getPlaceholderName(context));
    setSelectedItemPermission(getDefaultPermissionOption());
    setUsersDepartments(usersDepartmentsArray);
  }, [context]);

  useEffect(() => {
    const user_ids = userDepartments
      .filter((ud) => ud.type === EntityType.User)
      .map((item) => item.id);
    const department_ids = userDepartments
      .filter((ud) => ud.type === EntityType.Department)
      .map((item) => item.id);

    const basicFormData = {
      id: context?.id,
      name: name,
      query: context?.query,
      search_visibility: selectedItemPermission,
    };
    const formData = isSharedPermissionSelected
      ? {
          ...basicFormData,
          user_ids,
          department_ids,
        }
      : basicFormData;
    handleFormDataChanges(formData);
  }, [name, selectedItemPermission, userDepartments]);

  return (
    <FlexLayout flexDirection="column" space={3}>
      <InputLabel
        isRequired
        id="save-search-name"
        label={intl.formatMessage({
          id: 'SaveSearchForm.searchNameLabel',
          defaultMessage: 'Search name',
          description: 'Label for search name input field',
        })}
      >
        <TextInput
          autoFocus
          id="save-search--set-up-name"
          value={name}
          placeholder={intl.formatMessage({
            id: 'SaveSearchForm.enterNamePlaceholder',
            defaultMessage: 'Enter a name',
            description: 'Placeholder text for search name input',
          })}
          width="fullWidth"
          onChange={setName}
        />
        <FlexLayout justifyContent="flex-end">
          <CharacterLimit limit={CHARACTER_INPUT_LIMIT_SHORT} value={name} />
        </FlexLayout>
      </InputLabel>
      {hasSearchV2Flag && (
        <>
          <InputLabel
            isRequired
            id="save-search-permission-option"
            label={intl.formatMessage({
              id: 'SaveSearchForm.whoCanSeeUseSearchLabel',
              defaultMessage: 'Who can see and use this search?',
              description: 'Label for permission selection',
            })}
          >
            <SingleSelect
              isClearable={false}
              isSearchable={false}
              options={PERMISSION_OPTIONS}
              shouldHideSelectedOptions={false}
              value={selectedItemPermission}
              width="fullWidth"
              onChange={setSelectedItemPermission}
            />
          </InputLabel>
          {isSharedPermissionSelected && (
            <UserDepartmentSelect
              enablePortal={true}
              value={userDepartments}
              onChange={setUsersDepartments}
            />
          )}
        </>
      )}
    </FlexLayout>
  );
};

export default SaveSearchForm;
